/**
* Blog Tabs - Tab Switching & AJAX Pagination with URL State
* Handles switching between blog tabs without page reload
* Handles pagination within each tab via AJAX
* Updates URL for shareable links and browser history
*/

class BlogTabs {
  constructor(container) {
  this.container = container;
  this.tabs = container.querySelectorAll('.blog-tab');
  this.panels = container.querySelectorAll('.blog-tab-panel');
  this.indicator = container.querySelector('.blog-tabs-nav__indicator');
  this.nav = container.querySelector('.blog-tabs-nav');
  
  // ResizeObserver for tracking precise element dimensions changes
  this.resizeObserver = new ResizeObserver(() => {
  requestAnimationFrame(() => this.updateIndicatorPosition(false));
  });
  
  if (this.tabs.length > 0) {
  this.setupLabels();
  this.bindEvents();
  this.bindPaginationEvents();
  this.handlePopState();
  this.restoreStateFromURL();
  
  // We use requestAnimationFrame to ensure DOM is ready for calculations
  requestAnimationFrame(() => {
  this.updateIndicatorPosition(false);
  });
  
  // Recalculate after everything (images, fonts) is loaded
  window.addEventListener('load', () => {
  this.updateIndicatorPosition(false);
  });
  
  // Keep this for responsive recalculation
  window.addEventListener('resize', () => {
  this.updateIndicatorPosition(false);
  });
  
  // Update indicator on scroll (for horizontal scrolling)
  this.nav.addEventListener('scroll', () => {
  this.updateIndicatorPosition(false);
  });
  }
  }
  
  /**
  * Sets up data-label attribute on labels to match text content.
  * This is used by CSS to reserve space for the bold font-weight state,
  * preventing layout shifts ("shaking") when switching tabs.
  */
  setupLabels() {
  this.tabs.forEach(tab => {
  const label = tab.querySelector('.blog-tab__label');
  if (label && label.textContent) {
  // Determine the clean text content (trimming whitespace)
  // This attribute is used by CSS ::after pseudo-element
  label.setAttribute('data-label', label.textContent.trim());
  }
  });
  }
  
  
  /**
  * Updates the position and width of the active tab indicator (Yellow Shape).
  *
  * Logic:
  * - Mobile (<= 767px): Uses static CSS shape (no JS calculation).
  * - Desktop: Calculates position relative to the image and extends a "tail"
  * to the left.
  * The width wraps the content (Image to Text) + the tail extension.
  *
  * @param {boolean} animate - Whether to apply transition animation.
  */
  updateIndicatorPosition(animate = true) {
  // MOBILE OPTIMIZATION:
  // On mobile devices, we use a static CSS pseudo-element for performance and
  // to match specific design requirements (trapezoid shape).
  // Therefore, we exit early to skip JS calculations.
  if (window.innerWidth <= 767) return;
  
  const activeTab = this.container.querySelector('.blog-tab.active');
  // Safety check: ensure all necessary elements exist before proceeding
  if (!activeTab || !this.indicator || !this.nav) return;
  
  // --- 1. CONFIGURATION ---
  // TAIL_EXTENSION: Pixels to extend the indicator to the left of the image.
  // Matches the "short left tail" design request.
  const TAIL_EXTENSION = 15;
  
  // BUFFER_RIGHT: Extra padding on the right side of the text label.
  const BUFFER_RIGHT = 10;
  
  // --- 2. MEASUREMENTS ---
  const navRect = this.nav.getBoundingClientRect();
  const tabRect = activeTab.getBoundingClientRect();
  
  // Get sub-elements to calculate precise content bounds
  const label = activeTab.querySelector('.blog-tab__label');
  const imageWrapper = activeTab.querySelector('.blog-tab__image-wrapper');
  
  // Fallback to tabRect if specific elements are missing (rare edge case)
  const labelRect = label ? label.getBoundingClientRect() : tabRect;
  const imageRect = imageWrapper ? imageWrapper.getBoundingClientRect() :
  tabRect;
  
  const currentScroll = this.nav.scrollLeft;
  
  // --- 3. CALCULATION (DESKTOP) ---
  // We assume Horizontal Layout: [Image] -> [Text]
  
  // Start Point (Left):
  // We align relative to the *Image's Left Edge*.
  // Then we subtract the TAIL_EXTENSION to create the "tail" effect.
  const contentLeftAbs = imageRect.left;
  const finalLeft = (contentLeftAbs - navRect.left) + currentScroll -
  TAIL_EXTENSION;
  
  // End Point (Right) & Width:
  // Width covers the distance from the Image Left to the Label Right.
  // We add the TAIL_EXTENSION (since we shifted left) and the BUFFER_RIGHT.
  const contentRightAbs = labelRect.right;
  const baseContentWidth = contentRightAbs - contentLeftAbs;
  
  const finalWidth = TAIL_EXTENSION + baseContentWidth + BUFFER_RIGHT;
  
  // Vertical Alignment:
  // specific height (90px) and centered vertically.
  const finalTop = '50%';
  const transform = 'translateY(-50%)';
  const finalHeight = '90px';
  
  // --- 4. APPLY STYLES ---
  if (!animate) {
  this.nav.classList.add('blog-tabs-nav--no-transition');
  }

  this.nav.style.setProperty('--indicator-left', `${finalLeft}px`);
  this.nav.style.setProperty('--indicator-width', `${finalWidth}px`);

  if (!animate) {
  // Force reflow to flush style changes before re-enabling transitions
  this.indicator.offsetHeight;
  this.nav.classList.remove('blog-tabs-nav--no-transition');
  }
  }
  
  bindEvents() {
  this.tabs.forEach(tab => {
  tab.addEventListener('click', (e) => {
  e.preventDefault();
  this.switchTab(tab, true); // true = update URL
  });
  });
  
  // Arrow Navigation for Mobile (Re-query to ensure they are found)
  const prevArrow = this.container.querySelector('.blog-tabs-arrow--prev');
  const nextArrow = this.container.querySelector('.blog-tabs-arrow--next');
  
  if (prevArrow && nextArrow) {
  prevArrow.addEventListener('click', (e) => {
  e.preventDefault();
  e.stopPropagation(); // prevent bubbling issues
  const scrollAmount = this.nav.clientWidth / 3; // Scroll by one item
  this.nav.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  });
  
  nextArrow.addEventListener('click', (e) => {
  e.preventDefault();
  e.stopPropagation();
  const scrollAmount = this.nav.clientWidth / 3; // Scroll by one item
  this.nav.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  });
  
  // Initial check
  this.checkArrowVisibility(prevArrow, nextArrow);
  
  // Check on scroll
  this.nav.addEventListener('scroll', () => {
  this.checkArrowVisibility(prevArrow, nextArrow);
  });
  
  // Check on resize
  window.addEventListener('resize', () => {
  this.checkArrowVisibility(prevArrow, nextArrow);
  });
  }
  }
  
  checkArrowVisibility(prevArrow, nextArrow) {
  if (!this.nav) return;
  
  // Define tolerance usually 1px
  const tolerance = 2;
  const scrollLeft = this.nav.scrollLeft;
  const maxScrollLeft = this.nav.scrollWidth - this.nav.clientWidth;
  
  // Disable Left Arrow if at start
  if (scrollLeft <= tolerance) {
  prevArrow.setAttribute('disabled', 'true');
  prevArrow.classList.add('disabled');
  } else {
  prevArrow.removeAttribute('disabled');
  prevArrow.classList.remove('disabled');
  }
  
  // Disable Right Arrow if at end
  if (Math.abs(scrollLeft - maxScrollLeft) <= tolerance || maxScrollLeft <= 0)
  {
  nextArrow.setAttribute('disabled', 'true');
  nextArrow.classList.add('disabled');
  } else {
  nextArrow.removeAttribute('disabled');
  nextArrow.classList.remove('disabled');
  }
  }
  
  bindPaginationEvents() {
  // Bind click events to all pagination links within blog tab panels
  this.panels.forEach(panel => {
  const paginationWrapper = panel.querySelector('.blog-tab-pagination');
  if (paginationWrapper) {
  paginationWrapper.addEventListener('click', (e) => {
  const link = e.target.closest('a');
  if (link && link.href) {
  e.preventDefault();
  this.handlePagination(panel, link.href, true); // true = update URL
  }
  });
  }
  });
  }
  
  handlePopState() {
  // Handle browser back/forward buttons
  window.addEventListener('popstate', () => {
  this.restoreStateFromURL();
  });
  }
  
  restoreStateFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  const tabParam = urlParams.get('tab');
  const pageParam = urlParams.get('page') || '1';
  
  if (tabParam) {
  // Find the tab button for this blog
  const targetTab = Array.from(this.tabs).find(tab => tab.dataset.blog ===
  tabParam);
  if (targetTab) {
  this.switchTab(targetTab, false); // false = don't update URL (we're
  // restoring from URL)
  
  // If page > 1, fetch that page
  if (pageParam !== '1') {
  const panel = this.container.querySelector(`.blog-tab-panel[data-blog="${
  tabParam}"]`);
  if (panel && panel.dataset.blogUrl) {
  this.handlePagination(panel, `${panel.dataset.blogUrl}?page=${pageParam}`,
  false);
  }
  }
  }
  }
  }
  
  updateURL(blogHandle, page) {
  const url = new URL(window.location);
  
  if (blogHandle === 'all') {
  url.searchParams.delete('tab');
  url.searchParams.delete('page');
  } else {
  url.searchParams.set('tab', blogHandle);
  if (page && page !== '1') {
  url.searchParams.set('page', page);
  } else {
  url.searchParams.delete('page');
  }
  }
  
  history.pushState({ tab: blogHandle, page: page }, '', url);
  }
  
  switchTab(selectedTab, updateUrl = true) {
  const blogHandle = selectedTab.dataset.blog;
  
  // Update active tab styling
  this.tabs.forEach(tab => {
  tab.classList.remove('active');
  tab.setAttribute('aria-selected', 'false');
  });
  selectedTab.classList.add('active');
  selectedTab.setAttribute('aria-selected', 'true');
  
  // Update active panel visibility and reset to page 1
  this.panels.forEach(panel => {
  if (panel.dataset.blog === blogHandle) {
  panel.classList.add('active');
  panel.setAttribute('aria-hidden', 'false');
  
  // Reset to page 1 when switching tabs via user click (if not already on page 1).
  // Skip reset when called from restoreStateFromURL (updateUrl === false) —
  // that function fires its own handlePagination for the correct page, and
  // triggering two concurrent AJAX requests would cause a race condition.
  const contentWrapper = panel.querySelector('.blog-tab-content');
  if (contentWrapper) {
  const currentPage = parseInt(contentWrapper.dataset.currentPage || '1');
  if (currentPage !== 1 && updateUrl) {
  // Fetch page 1 for this blog
  const blogUrl = panel.dataset.blogUrl;
  if (blogUrl) {
  this.handlePagination(panel, blogUrl + '?page=1', false);
  }
  }
  }
  } else {
  panel.classList.remove('active');
  panel.setAttribute('aria-hidden', 'true');
  }
  });
  
  // Update URL
  if (updateUrl) {
  this.updateURL(blogHandle, '1');
  }
  
  // Observe the new active tab's label for size changes
  this.observeActiveTab(selectedTab);
  
  // Update indicator position
  this.updateIndicatorPosition();
  }
  
  observeActiveTab(tab) {
  // Disconnect previous observation
  this.resizeObserver.disconnect();
  
  // Observe the label of the active tab
  const label = tab.querySelector('.blog-tab__label');
  if (label) {
  this.resizeObserver.observe(label);
  }
  }
  
  async handlePagination(panel, url, updateUrl = true) {
  const blogHandle = panel.dataset.blog;
  const blogUrl = panel.dataset.blogUrl;
  
  // Extract page number from URL
  const urlObj = new URL(url, window.location.origin);
  const page = urlObj.searchParams.get('page') || '1';
  
  try {
  // Use ?view=loopie-new-blog to load blog-template.liquid directly via the
  // existing alternate template, bypassing blog.redirect.json (which returns
  // no article HTML, causing silent AJAX failure). Direct user visits to
  // /blogs/[handle] are unaffected — the redirect still fires for them.
  const response = await fetch(`${blogUrl}?view=loopie-new-blog&page=${page}`);
  
  if (!response.ok) {
  throw new Error('Network response was not ok');
  }
  
  const html = await response.text();
  
  // Parse the HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Find the articles from main-blog section
  const articlesFromPage = doc.querySelector('.blog-articles');
  const paginationFromPage = doc.querySelector('.page-blogs-pagination');
  
  if (articlesFromPage) {
  // Update articles in the panel
  const contentWrapper = panel.querySelector('.blog-tab-content');
  const articlesContainer = panel.querySelector('.blog-articles');
  const paginationContainer = panel.querySelector('.blog-tab-pagination');

  if (articlesContainer) {
  articlesContainer.innerHTML = articlesFromPage.innerHTML;
  // Page 1: 7 articles (1 featured + 6 grid) to match the initial server-rendered layout.
  // Pages 2+: 9 articles (all grid), matching blog-template.liquid's articles_per_page.
  const pageNum = parseInt(page, 10);
  const limit = pageNum === 1 ? 7 : (parseInt(panel.dataset.articlesLimit, 10) || 9);
  this.restructureToTabLayout(articlesContainer, pageNum, limit);
  }
  
  // Update pagination
  if (paginationContainer && paginationFromPage) {
  paginationContainer.innerHTML = paginationFromPage.innerHTML;
  // Re-bind pagination events for new links
  this.rebindPaginationForPanel(panel);
  } else if (paginationContainer && !paginationFromPage) {
  // No more pagination needed (single page)
  paginationContainer.innerHTML = '';
  }
  
  // Update current page data attribute
  if (contentWrapper) {
  contentWrapper.dataset.currentPage = page;
  }
  
  // Update URL
  if (updateUrl) {
  this.updateURL(blogHandle, page);
  }
  
  // Scroll to top of panel
  panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  } catch (error) {
  console.error('Error fetching blog page:', error);
  // Fallback: navigate to the URL normally
  window.location.href = url;
  }
  }
  
  /**
  * Escapes a string for safe insertion into HTML attribute/text contexts.
  */
  escapeHtml(str) {
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(String(str)));
  return div.innerHTML;
  }

  /**
  * Validates a URL is same-origin or a safe relative path.
  * Returns '#' for any URL that fails validation.
  */
  sanitizeUrl(url) {
  if (!url || url === '#') return '#';
  try {
  const parsed = new URL(url, window.location.origin);
  if (parsed.origin === window.location.origin) {
  return parsed.pathname + parsed.search + parsed.hash;
  }
  } catch (_) {}
  return '#';
  }

  /**
  * After AJAX pagination replaces .blog-articles innerHTML with flat
  * article-card-wrapper elements (from blog-template.liquid), this method
  * rebuilds the proper featured-card + grid-cards structure used by our CSS.
  *
  * Extracts: image, URL, title, date, excerpt from each article-card element.
  */
  restructureToTabLayout(container, page = 1, limit = 0) {
  // blog-template.liquid always renders the first article on a page as
  // .page-blogs__featured (not .article-card-wrapper), so we must collect
  // both sources before slicing to the correct limit.
  const articles = [];

  // 1. Pull the "featured" article from blog-template (if present)
  const featuredEl = container.querySelector('.page-blogs-featured');
  if (featuredEl) {
    const imgEl = featuredEl.querySelector('.page-blogs-featured-image-wrapper img');
    const titleLinkEl = featuredEl.querySelector('.page-blogs-featured-title a');
    const timeEl = featuredEl.querySelector('time');
    const excerptEl = featuredEl.querySelector('.page-blogs-featured-excerpt');
    articles.push({
      imgSrc: imgEl ? imgEl.src : '',
      imgAlt: this.escapeHtml(imgEl ? (imgEl.alt || '') : ''),
      articleUrl: this.sanitizeUrl(titleLinkEl ? titleLinkEl.getAttribute('href') : '#'),
      titleText: this.escapeHtml(titleLinkEl ? titleLinkEl.textContent.trim() : ''),
      dateStr: this.escapeHtml(timeEl ? timeEl.textContent.trim().toUpperCase() : ''),
      excerptText: this.escapeHtml(excerptEl ? excerptEl.textContent.trim() : ''),
    });
  }

  // 2. Pull the remaining articles from .article-card-wrapper elements
  container.querySelectorAll('.article-card-wrapper').forEach(card => {
    const imgEl = card.querySelector('img');
    const titleLinkEl = card.querySelector('.card__heading a, h3 a');
    const timeEl = card.querySelector('time');
    const excerptEl = card.querySelector('.article-card__excerpt, p[class*="excerpt"]');
    articles.push({
      imgSrc: imgEl ? imgEl.src : '',
      imgAlt: this.escapeHtml(imgEl ? (imgEl.alt || '') : ''),
      articleUrl: this.sanitizeUrl(titleLinkEl ? titleLinkEl.getAttribute('href') : '#'),
      titleText: this.escapeHtml(titleLinkEl ? titleLinkEl.textContent.trim() : ''),
      dateStr: this.escapeHtml(timeEl ? timeEl.textContent.trim().toUpperCase() : ''),
      excerptText: this.escapeHtml(excerptEl ? excerptEl.textContent.trim() : ''),
    });
  });

  if (!articles.length) return;

  // 3. Slice to the configured limit
  const limited = limit > 0 ? articles.slice(0, limit) : articles;

  // Featured horizontal card only appears on page 1
  const showFeatured = page === 1;
  const htmlParts = [];
  let hasGrid = false;

  limited.forEach((a, index) => {
  if (index === 0 && showFeatured) {
  htmlParts.push(`
  <div class="blog-tab-featured">
  ${a.imgSrc ? `
  <a href="${a.articleUrl}" class="blog-tab-featured-image-link" tabindex="-1" aria-hidden="true">
  <div class="blog-tab-featured-image-wrapper">
  <img src="${a.imgSrc}" alt="${a.imgAlt}" loading="lazy" class="blog-tab-featured-img">
  </div>
  </a>` : '<div class="blog-tab-featured-image-placeholder"></div>'}
  <div class="blog-tab-featured-meta">
  <span class="blog-tab-featured-badge">Newly Published</span>
  ${a.dateStr ? `<time class="blog-tab-featured-date">${a.dateStr}</time>` : ''}
  <h2 class="blog-tab-featured-title"><a href="${a.articleUrl}">${a.titleText}</a></h2>
  </div>
  ${a.excerptText ? `<p class="blog-tab-featured-excerpt">${a.excerptText}</p>` : ''}
  <a href="${a.articleUrl}" class="blog-tab-featured-link">Read More</a>
  </div>
  `);
  } else {
  if (!hasGrid) {
  htmlParts.push('<div class="blog-tab-grid">');
  hasGrid = true;
  }
  htmlParts.push(`
  <div class="blog-tab-card">
  ${a.imgSrc ? `
  <a href="${a.articleUrl}" class="blog-tab-card-image-link" tabindex="-1" aria-hidden="true">
  <div class="blog-tab-card-image-wrapper">
  <img src="${a.imgSrc}" alt="${a.imgAlt}" loading="lazy" class="blog-tab-card-img">
  </div>
  </a>` : ''}
  <div class="blog-tab-card-body">
  ${a.dateStr ? `<time class="blog-tab-card-date">${a.dateStr}</time>` : ''}
  <h3 class="blog-tab-card-title"><a href="${a.articleUrl}">${a.titleText}</a></h3>
  ${a.excerptText ? `<p class="blog-tab-card-excerpt">${a.excerptText}</p>` : ''}
  <a href="${a.articleUrl}" class="blog-tab-card-link">Read More</a>
  </div>
  </div>
  `);
  }
  });

  if (hasGrid) htmlParts.push('</div>');
  container.innerHTML = htmlParts.join('');
  }

  rebindPaginationForPanel(panel) {
  const paginationWrapper = panel.querySelector('.blog-tab-pagination');
  if (paginationWrapper) {
  // Remove existing listeners by cloning
  const newPaginationWrapper = paginationWrapper.cloneNode(true);
  paginationWrapper.parentNode.replaceChild(newPaginationWrapper,
  paginationWrapper);
  
  // Add new listener
  newPaginationWrapper.addEventListener('click', (e) => {
  const link = e.target.closest('a');
  if (link && link.href) {
  e.preventDefault();
  this.handlePagination(panel, link.href, true);
  }
  });
  }
  }
  }
  
  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', () => {
  const blogTabsWrappers = document.querySelectorAll('.blog-tabs-wrapper');
  
  blogTabsWrappers.forEach(wrapper => {
  new BlogTabs(wrapper);
  });
  });
  
  // Re-initialize on Shopify section reload (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const wrapper = section.querySelector('.blog-tabs-wrapper');
  
  if (wrapper) {
  new BlogTabs(wrapper);
  }
  });
  }
  